import { flg } from "@aix/webutils/src/ui/logging"
import { reportErrorToSentry } from "@aix/webutils/src/sentry"
import { EVMAnalyticsEvent } from "@aix/vidmax-shared/src/analytics"
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { PageLoading } from "~/components/PageLoading"
import { useAuthSession } from "~/hooks/useAuthSession"
import { DEFAULT_POST_AUTH_DEST, extractCallbackPath } from "~/lib/authConfig"
import { usePH } from "~/utils/posthogClient"
import z from "zod"

const callbackSearchSchema = z.object({
  callbackUrl: z.string().optional(),
  error: z.string().optional(),
  code: z.string().optional(),
  state: z.string().optional(),
})

function AuthCallbackPage() {
  const navigate = useNavigate()
  const search = useSearch({ from: "/auth/callback" })
  const { status, session } = useAuthSession()
  const { capture } = usePH()
  const [isProcessing, setIsProcessing] = useState(true)
  const [error, setError] = useState<string | null>(null)

  flg.info("🎯 [AuthCallback] Page loaded:")

  useEffect(() => {
    const processCallback = async () => {
      flg.info("🔄 [AuthCallback] Processing callback:")

      try {
        // Check if there's an error from the OAuth provider
        if (search.error) {
          flg.error("❌ [AuthCallback] OAuth error:")
          setError(`Authentication failed: ${search.error}`)
          setIsProcessing(false)
          return
        }

        flg.info("⏳ [AuthCallback] Waiting for session establishment...")
        // Wait a moment for the session to be established
        // Auth.js should have already processed the callback and created the session
        await new Promise((resolve) => setTimeout(resolve, 1000))

        flg.info("🔍 [AuthCallback] Checking session after wait:")

        // Check session status
        if (status === "authenticated") {
          // Redirect to the intended destination
          const callbackUrl = search.callbackUrl || DEFAULT_POST_AUTH_DEST
          const navigationPath = extractCallbackPath(callbackUrl)
          flg.info("✅ [AuthCallback] Authenticated! Redirecting to:")
          capture(EVMAnalyticsEvent.SIGN_IN, {
            auth_method: "google",
            destination: navigationPath,
            redirect_source: search.callbackUrl ? "callback_url" : "default",
            add: { total_signins: 1 },
          })
          void navigate({ to: navigationPath, replace: true })
        } else if (status === "unauthenticated") {
          // Session wasn't created successfully
          flg.error("❌ [AuthCallback] Authentication failed - no session created")
          setError("Authentication failed. Please try again.")
        } else {
          flg.info("⏳ [AuthCallback] Still loading, waiting...")
        }
        // If still loading, keep waiting
      } catch (err) {
        void reportErrorToSentry(err, "authCallback.processCallback")
        setError("An error occurred during authentication.")
      } finally {
        setIsProcessing(false)
      }
    }

    // Only process if we have search params or if status has changed
    if (search || status !== "loading") {
      void processCallback()
    }
  }, [search, status, navigate, session])

  // Show error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Authentication Error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate({ to: "/auth/signin" })}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  // Show loading state while processing
  if (isProcessing || status === "loading") {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <PageLoading />
        <p className="mt-4 text-gray-600">Completing sign in...</p>
      </div>
    )
  }

  // If we get here, something went wrong
  return (
    <div className="flex flex-col items-center justify-center min-h-screen">
      <div className="text-center max-w-md">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Authentication Complete</h1>
        <p className="text-gray-600 mb-6">Redirecting you now...</p>
      </div>
    </div>
  )
}

export const Route = createFileRoute("/auth/callback")({
  validateSearch: callbackSearchSchema,
  component: AuthCallbackPage,
})
